import React from 'react'
import { useOutletContext } from 'react-router-dom'
import { Card, Typography, Flex, Form, Input, Select, message } from 'antd'
import { CopyOutlined } from '@ant-design/icons'
import { getFunnelsURL } from '@/api/client'
import type { TestEditorContext } from '../../index'

const { Title, Text } = Typography

export const TestInfoPage: React.FC = () => {
  const {
    testName,
    setTestName,
    baseFlowId,
    setBaseFlowId,
    leadFlows,
    setLeadFlows,
    availableFlows,
  } = useOutletContext<TestEditorContext>()

  const testFlowIds = leadFlows.map((f) => f.flow_id)

  const handleFlowChange = (flowIds: string[]) => {
    if (flowIds.length === 0) {
      setLeadFlows([])
      return
    }

    const equalShare = Math.floor(100 / flowIds.length)
    const remainder = 100 - equalShare * flowIds.length

    setLeadFlows(
      flowIds.map((id, index) => {
        const existing = leadFlows.find((f) => f.flow_id === id)
        const flow = availableFlows.find((f) => String(f.id) === id)
        return {
          flow_id: id,
          flow_name: existing?.flow_name || flow?.name || id,
          traffic_percentage:
            existing?.traffic_percentage ??
            equalShare + (index === flowIds.length - 1 ? remainder : 0),
        }
      }),
    )
  }

  const handleBaseFlowChange = (value: string | undefined) => {
    const newBaseId = value ?? null
    setBaseFlowId(newBaseId)

    if (newBaseId && testFlowIds.includes(newBaseId)) {
      handleFlowChange(testFlowIds.filter((id) => id !== newBaseId))
    }
  }

  return (
    <div>
      <Flex vertical gap="large" style={{ width: '100%' }}>
        <div>
          <Title level={4}>Test Information</Title>
          <Text type="secondary">
            Name your test and select the lead flows to compare.
          </Text>
        </div>

        <Card title="Test Details" style={{ maxWidth: 700 }}>
          <Form layout="vertical">
            <Form.Item label="Test Name">
              <Input
                value={testName}
                onChange={(e) => setTestName(e.target.value)}
                placeholder="Enter test name"
                size="large"
              />
            </Form.Item>

            <Form.Item label="Control Flow">
              <Select
                showSearch
                allowClear
                placeholder="Select a control flow..."
                value={baseFlowId}
                onChange={handleBaseFlowChange}
                style={{ width: '100%' }}
                size="large"
                optionFilterProp="label"
                options={availableFlows.map((flow) => ({
                  value: String(flow.id),
                  label: flow.name,
                }))}
              />
            </Form.Item>

            {baseFlowId &&
              (() => {
                const baseFlow = availableFlows.find(
                  (f) => String(f.id) === baseFlowId,
                )
                if (!baseFlow) return null
                const testUrl = `${getFunnelsURL()}/_/${baseFlow.slug}`
                return (
                  <Form.Item label="Test URL">
                    <Input
                      readOnly
                      value={testUrl}
                      size="large"
                      addonAfter={
                        <CopyOutlined
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            navigator.clipboard.writeText(testUrl)
                            message.success('URL copied')
                          }}
                        />
                      }
                    />
                  </Form.Item>
                )
              })()}

            <Form.Item label="Test Flows">
              <Select
                mode="multiple"
                placeholder="Select test flows to compare..."
                value={testFlowIds}
                onChange={handleFlowChange}
                style={{ width: '100%' }}
                size="large"
                optionFilterProp="label"
                options={availableFlows
                  .filter((flow) => String(flow.id) !== baseFlowId)
                  .map((flow) => ({
                    value: String(flow.id),
                    label: flow.name,
                  }))}
              />
              {testFlowIds.length > 0 && (
                <Text type="secondary" style={{ fontSize: 12, marginTop: 4 }}>
                  {testFlowIds.length} flow
                  {testFlowIds.length !== 1 ? 's' : ''} selected — traffic will
                  be split equally by default
                </Text>
              )}
            </Form.Item>
          </Form>
        </Card>
      </Flex>
    </div>
  )
}
